"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Treatment } from "@/lib/treatments-data";
import Breadcrumbs from "@/components/Breadcrumbs";
import HeroSection from "./teeth-whitening/hero-section";
import WhyChooseWhiteningSection from "./teeth-whitening/why-choose-whitening-section";
import WhiteningTypesSection from "./teeth-whitening/whitening-types-section";
import WhiteningProcessSection from "./teeth-whitening/whitening-process-section";
import CandidatesSection from "./teeth-whitening/candidates-section";
import PricingPackagesSection from "./teeth-whitening/pricing-packages-section";
import MaintenanceSection from "./teeth-whitening/maintenance-section";
import FAQSection from "./teeth-whitening/faq-section";
import TestimonialsSection from "./teeth-whitening/testimonials-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useTranslation } from "@/hooks/useTranslation";

gsap.registerPlugin(ScrollTrigger);

interface TeethWhiteningPageProps {
  treatment: Treatment;
}

export default function TeethWhiteningPage({
  treatment,
}: TeethWhiteningPageProps) {
  const { t, locale } = useTranslation('teeth-whitening');
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const sections = [
    { id: "hero", label: t('navigation.home') },
    { id: "why-whitening", label: t('navigation.why_whitening') },
    { id: "whitening-types", label: t('navigation.types') },
    { id: "process", label: t('navigation.process') },
    { id: "candidates", label: t('navigation.candidates') },
    { id: "pricing", label: t('navigation.pricing') },
    { id: "maintenance", label: t('navigation.maintenance') },
    { id: "testimonials", label: t('navigation.testimonials') },
    { id: "faq", label: t('navigation.faq') },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('navigation.back_to_treatments')}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={sections} />

      <main className="relative">
        <HeroSection treatment={treatment} />
        <WhyChooseWhiteningSection />
        <WhiteningTypesSection />
        <WhiteningProcessSection />
        <CandidatesSection />
        <PricingPackagesSection />
        <MaintenanceSection />
        <TestimonialsSection />
        <FAQSection />
        <FinalCTASection
          treatmentName={t('cta.treatment_name')}
          treatmentKeyword="teeth whitening"
          scrollToSection="maintenance"
          namespace="teeth-whitening"
          customHeading={
            <>
              {t('cta.custom_heading_part1')}{" "}
              <span className="text-gold">{t('cta.custom_heading_part2')}</span>
            </>
          }
          customSubheading={t('cta.custom_subheading')}
          customBenefits={[
            t('cta.benefits.0'),
            t('cta.benefits.1'),
            t('cta.benefits.2'),
          ]}
        />
      </main>

    </div>
  );
}